import React, { type ChangeEvent, useCallback, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Toggle from '@atlaskit/toggle';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { LEARN_MORE_ABOUT_TIMELINE } from '../../../../../common/constants';
import type { ThirdPartyFieldId } from '../../../../../common/constants/fields';
import type { ThirdPartyFieldMapping } from '../../../../../common/types';
import { InfoTooltip } from '../../../../../common/ui/info-tooltip';
import {
	ASANA_ASSIGNEE_FIELD_ID,
	ASANA_DUE_ON_FIELD_ID,
	ASANA_LIKES_FIELD_ID,
	ASANA_START_DATE_FIELD_ID,
} from '../../../../../common/utils/third-party-clients/asana/constants';
import {
	CLICKUP_DESCRIPTION,
	CLICKUP_START_DATE,
} from '../../../../../common/utils/third-party-clients/clickup/constants';
import { useThirdPartyFieldConfigState } from '../../../../../controllers/import-state';
import { useUIState } from '../../../../../controllers/ui-state';
import messages from './messages';

type Props = {
	thirdPartyFieldMapping: ThirdPartyFieldMapping;
};

const useWarningMessage = (
	fieldId: ThirdPartyFieldId,
	isToggleDisabled: boolean,
	isIncluded: boolean,
) => {
	const { formatMessage } = useIntl();

	switch (fieldId) {
		case ASANA_ASSIGNEE_FIELD_ID:
			return !isIncluded && formatMessage(messages.excludeAssignee);
		case ASANA_DUE_ON_FIELD_ID:
			return (
				!isIncluded &&
				formatMessage(messages.excludeDueDate, {
					link: (chunks: ReactNode) => (
						<a href={LEARN_MORE_ABOUT_TIMELINE} target="_blank">
							{chunks}
						</a>
					),
				})
			);
		case ASANA_START_DATE_FIELD_ID:
			return (
				!isIncluded &&
				formatMessage(messages.excludeStartDate, {
					link: (chunks: ReactNode) => (
						<a href={LEARN_MORE_ABOUT_TIMELINE} target="_blank">
							{chunks}
						</a>
					),
				})
			);
		case ASANA_LIKES_FIELD_ID:
			return !isIncluded && isToggleDisabled && formatMessage(messages.votesDisableMessage);
		case CLICKUP_DESCRIPTION:
			return isIncluded && formatMessage(messages.clickupDescriptionRichText);
		case CLICKUP_START_DATE:
			return isIncluded && formatMessage(messages.clickupStartDateRecurring);
		default:
			return undefined;
	}
};

const IncludeInImport = ({ thirdPartyFieldMapping }: Props) => {
	const warningMessage = useWarningMessage(
		thirdPartyFieldMapping.thirdPartyField.fieldId,
		thirdPartyFieldMapping.mappingContext.isToggleDisabled,
		thirdPartyFieldMapping.mappingContext.isIncluded,
	);
	const [, { setIsThirdPartyFieldIncluded }] = useThirdPartyFieldConfigState();
	const [, { setUIState }] = useUIState();
	const handleToggleChange = useCallback(
		(value: ChangeEvent<HTMLInputElement>) => {
			setIsThirdPartyFieldIncluded(
				thirdPartyFieldMapping.thirdPartyField.fieldId,
				value.target.checked,
			);
			setUIState({ hasUnsavedFieldMappingChanges: true });
		},
		[setIsThirdPartyFieldIncluded, thirdPartyFieldMapping.thirdPartyField.fieldId, setUIState],
	);

	return (
		<Wrapper>
			<Toggle
				testId="project-create-importer-upload-flow.ui.third-party-review-screen.fields-mapping.fields-mapping-content.include-in-import.toggle"
				isChecked={thirdPartyFieldMapping.mappingContext.isIncluded}
				isDisabled={
					thirdPartyFieldMapping.mappingContext.isRequired ||
					thirdPartyFieldMapping.mappingContext.isToggleDisabled
				}
				onChange={handleToggleChange}
				label="include-in-import-toggle"
			/>
			{warningMessage && <InfoTooltip tooltipContent={warningMessage} />}
		</Wrapper>
	);
};

export default IncludeInImport;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	marginLeft: token('space.400', '32px'),
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		marginLeft: token('space.100', '8px'),
	},
});
